// A card that appears when a reader RESTS on the name of a thing.
//
// Three quarters of a second, which is long enough that moving the pointer
// across a table of six hundred cells never fires one, and short enough that
// stopping on a name feels answered. It is also long enough to fetch the
// sentence, which is why five hundred summaries are not in the map's payload.
//
// Delegated from the document rather than mounted per element, because the
// same behaviour has to serve the server-rendered tables and the React ones
// and neither should have to know about it: anything carrying
// `data-correspondence-key` gets a card.
const DELAY = 750;
// A node on the map is a deliberate thing to point at; a cell in a table of
// six hundred is often just on the way past. `data-hover-delay` lets the map
// answer sooner without making the tables twitchy.
const delayFor = (anchor) => Number(anchor.dataset.hoverDelay) || DELAY;
const cache = new Map();

let card = null;
let timer = null;
let current = null;

function ensureCard() {
  if (card) return card;
  card = document.createElement("div");
  card.className = "hovercard";
  card.id = "correspondence-hovercard";
  // A tooltip, so a screen reader announces it for the thing it describes
  // rather than as a stray region.
  card.setAttribute("role", "tooltip");
  card.hidden = true;
  document.body.appendChild(card);
  return card;
}

function place(anchor) {
  const box = anchor.getBoundingClientRect();
  const held = ensureCard();
  held.hidden = false;
  const own = held.getBoundingClientRect();
  // Below by default, above when there is no room — the card must never be
  // the thing that makes a table scroll.
  const below = box.bottom + 8;
  const room = window.innerHeight - box.bottom;
  const top = room > own.height + 16 ? below : Math.max(8, box.top - own.height - 8);
  const left = Math.min(
    Math.max(8, box.left),
    Math.max(8, window.innerWidth - own.width - 8)
  );
  held.style.top = `${top + window.scrollY}px`;
  held.style.left = `${left + window.scrollX}px`;
}

function render(anchor, described) {
  const held = ensureCard();
  held.textContent = "";

  const name = document.createElement("p");
  name.className = "hovercard__name";
  name.textContent = described?.name || anchor.textContent.trim();
  held.appendChild(name);

  const kind = document.createElement("p");
  kind.className = "hovercard__kind";
  kind.textContent = described?.kind || "";
  held.appendChild(kind);

  // Already on the page, so it shows immediately and survives a failed fetch.
  if (anchor.dataset.attested) {
    const when = document.createElement("p");
    when.className = "hovercard__attested";
    when.textContent = anchor.dataset.attested;
    held.appendChild(when);
  }

  if (described?.summary) {
    const body = document.createElement("p");
    body.className = "hovercard__body";
    body.textContent = described.summary;
    held.appendChild(body);
  }

  anchor.setAttribute("aria-describedby", held.id);
  place(anchor);
}

function hide() {
  clearTimeout(timer);
  timer = null;
  if (current) current.removeAttribute("aria-describedby");
  current = null;
  if (card) card.hidden = true;
}

async function describe(key) {
  if (cache.has(key)) return cache.get(key);
  try {
    const response = await fetch(`/correspondences/describe?keys=${encodeURIComponent(key)}`, {
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error(response.status);
    const body = await response.json();
    cache.set(key, body[key] || null);
  } catch {
    // The name and the kind are already on the page; a failed fetch loses the
    // sentence and nothing else.
    cache.set(key, null);
  }
  return cache.get(key);
}

function open(anchor) {
  const key = anchor.dataset.correspondenceKey;
  if (!key) return;
  clearTimeout(timer);
  timer = setTimeout(async () => {
    const described = await describe(key);
    // The pointer may have moved on while that was in flight.
    if (current !== anchor) return;
    render(anchor, described);
  }, delayFor(anchor));
  current = anchor;
}

export function start() {
  if (typeof document === "undefined") return;

  document.addEventListener("pointerover", (event) => {
    const anchor = event.target.closest?.("[data-correspondence-key]");
    if (!anchor || anchor === current) return;
    hide();
    open(anchor);
  });
  document.addEventListener("pointerout", (event) => {
    if (event.target.closest?.("[data-correspondence-key]") === current) hide();
  });
  // The keyboard gets it too, and immediately: somebody tabbing to a cell has
  // already committed to it.
  document.addEventListener("focusin", (event) => {
    const anchor = event.target.closest?.("[data-correspondence-key]");
    if (!anchor) return hide();
    hide();
    current = anchor;
    describe(anchor.dataset.correspondenceKey).then((described) => {
      if (current === anchor) render(anchor, described);
    });
  });
  document.addEventListener("keydown", (event) => { if (event.key === "Escape") hide(); });
  window.addEventListener("scroll", hide, { passive: true });
}

export const HOVER_DELAY = DELAY;
